import React from "react";
import { Link } from "react-router-dom";
import {
  ArrowRight,
  Sliders,
  FileDown,
  Share2,
  Sparkles,
  Timer,
  Check,
} from "lucide-react";

const features = [
  {
    icon: Sliders,
    title: "Mast individuell zusammenstellen",
    text: "Höhe, Bauart (starr oder kippbar), Durchmesser und Farbe – Sie steuern jedes Detail.",
  },
  {
    icon: Sparkles,
    title: "Zubehör direkt mitplanen",
    text: "Kameraanschlusskasten, PoE-Switch, Glasfaser, Überspannungsschutz und Fundament in einem Rutsch.",
  },
  {
    icon: FileDown,
    title: "Angebot & PDF auf Knopfdruck",
    text: "Am Ende erhalten Sie eine PDF-Zusammenfassung und können direkt eine Anfrage an unser Vertriebsteam senden.",
  },
  {
    icon: Share2,
    title: "Konfigurationscode zum Teilen",
    text: "Ihre Auswahl wird als Code gespeichert – jederzeit erneut aufrufen, weiterbearbeiten oder mit Kollegen teilen.",
  },
];

const steps = [
  "Mast-Variante wählen",
  "Anschlusstechnik konfigurieren",
  "Zubehör ergänzen",
  "PDF & Anfrage",
];

export default function KonfiguratorSection() {
  return (
    <section
      id="konfigurator-teaser"
      data-testid="konfigurator-section"
      className="relative bg-gradient-to-br from-[#0B1220] via-[#0B1220] to-[#0055A4]/40 text-white py-24 md:py-32 overflow-hidden"
    >
      <div className="absolute inset-0 bg-grid-dark opacity-30 pointer-events-none" />
      <div
        className="absolute -left-32 top-1/2 -translate-y-1/2 h-96 w-96 rounded-full bg-[#5BA8FF]/10 blur-3xl"
        aria-hidden
      />

      <div className="relative max-w-7xl mx-auto px-6 md:px-10">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-16 items-center">
          {/* Text side */}
          <div className="lg:col-span-6">
            <p className="tag-line text-xs font-mono-tech uppercase tracking-[0.25em] text-[#5BA8FF] mb-6">
              Neu · Online-Konfigurator
            </p>
            <h2 className="font-display text-3xl sm:text-4xl lg:text-5xl font-semibold tracking-tight">
              Ihr Kameramast –{" "}
              <span className="text-[#5BA8FF]">in wenigen Minuten konfiguriert.</span>
            </h2>
            <p className="mt-6 text-white/80 text-base sm:text-lg leading-relaxed max-w-xl">
              Der Xortec Mastkonfigurator führt Sie in klaren Schritten
              von der Mastvariante bis zum vollständigen Systempaket – inklusive
              Anschlusskasten, aktiver Netzwerktechnik und Zubehör.
            </p>

            <ul className="mt-10 grid grid-cols-1 sm:grid-cols-2 gap-6">
              {features.map((f) => (
                <li
                  key={f.title}
                  data-testid={`konfig-feature-${f.title.slice(0, 20)}`}
                  className="flex gap-3"
                >
                  <span className="mt-0.5 inline-flex h-9 w-9 shrink-0 items-center justify-center rounded-lg bg-white/10 border border-white/15 text-[#5BA8FF]">
                    <f.icon className="h-4 w-4" />
                  </span>
                  <div>
                    <div className="font-display font-medium text-sm text-white">
                      {f.title}
                    </div>
                    <div className="mt-1 text-xs text-white/65 leading-relaxed">
                      {f.text}
                    </div>
                  </div>
                </li>
              ))}
            </ul>

            <div className="mt-10 flex flex-col sm:flex-row gap-4 sm:items-center">
              <Link
                to="/konfigurator"
                data-testid="konfig-cta-open"
                className="group inline-flex items-center justify-center gap-2 bg-[#0055A4] hover:bg-[#003e7a] text-white px-7 py-3.5 rounded-md font-medium transition-colors shadow-lg shadow-[#0055A4]/30"
              >
                Jetzt Kameramast konfigurieren
                <ArrowRight className="h-4 w-4 group-hover:translate-x-0.5 transition-transform" />
              </Link>
              <div className="flex items-center gap-2 text-sm text-white/60">
                <Timer className="h-4 w-4 text-[#5BA8FF]" />
                Dauer: ca. 3&nbsp;Minuten
              </div>
            </div>
          </div>

          {/* Preview / diagram side */}
          <div className="lg:col-span-6">
            <div className="relative rounded-2xl border border-white/10 bg-white/[0.03] backdrop-blur-sm p-6 md:p-8 shadow-2xl">
              {/* Browser chrome */}
              <div className="flex items-center gap-2 mb-5">
                <span className="h-2.5 w-2.5 rounded-full bg-red-500/80" />
                <span className="h-2.5 w-2.5 rounded-full bg-yellow-500/80" />
                <span className="h-2.5 w-2.5 rounded-full bg-emerald-500/80" />
                <span className="ml-3 font-mono-tech text-[10px] text-white/40 truncate">
                  kameramasten-tool.emergent.host
                </span>
              </div>

              {/* Fake configurator card */}
              <div className="rounded-xl bg-[#0B1220]/60 border border-white/10 p-6">
                <div className="font-mono-tech text-[10px] text-[#5BA8FF] tracking-widest mb-2">
                  SCHRITT 1 / 4
                </div>
                <div className="font-display text-xl font-semibold text-white">
                  Mastvariante wählen
                </div>

                <div className="mt-5 space-y-3">
                  <div className="flex items-center justify-between rounded-lg border border-[#5BA8FF]/40 bg-[#0055A4]/20 px-4 py-3">
                    <div>
                      <div className="text-sm font-medium text-white">
                        Kippmast · 8&nbsp;m
                      </div>
                      <div className="text-xs text-white/50">
                        Aluminium · Ø 152 mm · pulverbeschichtet
                      </div>
                    </div>
                    <span className="inline-flex h-6 w-6 items-center justify-center rounded-full bg-[#5BA8FF] text-[#0B1220]">
                      <Check className="h-3.5 w-3.5" strokeWidth={3} />
                    </span>
                  </div>
                  <div className="flex items-center justify-between rounded-lg border border-white/10 px-4 py-3">
                    <div>
                      <div className="text-sm font-medium text-white/85">
                        Starrer Mast · 6&nbsp;m
                      </div>
                      <div className="text-xs text-white/40">
                        Aluminium · Ø 120 mm
                      </div>
                    </div>
                    <span className="text-xs text-white/40 font-mono-tech">
                      Wählen
                    </span>
                  </div>
                  <div className="flex items-center justify-between rounded-lg border border-white/10 px-4 py-3">
                    <div>
                      <div className="text-sm font-medium text-white/85">
                        Kippmast · 12&nbsp;m
                      </div>
                      <div className="text-xs text-white/40">
                        Aluminium · Ø 200 mm
                      </div>
                    </div>
                    <span className="text-xs text-white/40 font-mono-tech">
                      Wählen
                    </span>
                  </div>
                </div>

                {/* Progress dots */}
                <div className="mt-6 flex items-center gap-2">
                  {steps.map((s, i) => (
                    <div key={s} className="flex items-center gap-2 flex-1">
                      <span
                        className={`h-1 flex-1 rounded-full ${
                          i === 0 ? "bg-[#5BA8FF]" : "bg-white/10"
                        }`}
                      />
                    </div>
                  ))}
                </div>
                <div className="mt-3 flex justify-between text-[10px] font-mono-tech uppercase tracking-widest text-white/40">
                  {steps.map((s) => (
                    <span key={s}>{s}</span>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
